/**
 * Page de présentation de l'association Crions d'Couleurs
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';
import './PresentationAssociation.css';

const PresentationAssociation = () => {
  const [statsAnimated, setStatsAnimated] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const statsRef = useRef(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';

    // Gérer le scroll vers une section si on arrive avec une ancre dans l'URL
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const sectionId = hash.replace('#', '');
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }

    // Observer pour animer les statistiques
    const observerOptions = {
      threshold: 0.5,
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !statsAnimated) {
          animateStatNumbers();
          setStatsAnimated(true);
        }
      });
    }, observerOptions);

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    // Navbar scroll effect
    const handleScroll = () => {
      const scrollPosition = window.pageYOffset;

      // Change navbar background on scroll
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [statsAnimated]);

  const animateStatNumbers = () => {
    const statNumbers = document.querySelectorAll('.stat-number');

    statNumbers.forEach(statNumber => {
      const text = statNumber.textContent;

      // Skip animation for infinity symbol
      if (text === '∞') return;

      // Only animate the "+1 100" (enfants partis en colo)
      // Skip other numbers (7, 2010, etc.)
      if (!text.match(/^\+/)) return;

      // Extract number from text (e.g., "+1 100" -> 1100)
      const numberMatch = text.match(/[\d\s]+/);
      if (!numberMatch) return;

      const finalNumber = parseInt(numberMatch[0].replace(/\s/g, ''));
      const prefix = '+';
      const duration = 2000; // 2 seconds
      const steps = 60;
      const increment = finalNumber / steps;
      let currentNumber = 0;
      let step = 0;

      const interval = setInterval(() => {
        step++;
        currentNumber = Math.min(currentNumber + increment, finalNumber);

        // Format number with space separator for thousands
        const formattedNumber = Math.floor(currentNumber)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

        statNumber.textContent = prefix + formattedNumber;

        if (step >= steps) {
          clearInterval(interval);
          // Set final value
          const finalFormatted = finalNumber
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
          statNumber.textContent = prefix + finalFormatted;
        }
      }, duration / steps);
    });
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <div className="presentation-page">
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Association - Crions d'Couleurs</title>
        <meta
          name="description"
          content="Crions d'Couleurs organise des colonies de vacances accessibles pour jeunes de 6-17 ans en Ille-et-Vilaine. Association de jeunes du Pays de Vitré engagée dans la mixité sociale. Séjours de vacances pas chers en Bretagne."
        />
        <meta
          name="keywords"
          content="colonie de vacances Vitré, colo jeunes Bretagne, séjour de vacances, association colo, colonie de vacances pas chère, engagement jeunes Vitré, colonie de vacances accessible, colo Ille-et-Vilaine, séjour enfants Bretagne, colonies été Vitré, association jeunesse Bretagne, colo mixité sociale, séjour vacances 6-17 ans"
        />
        <meta name="author" content="Crions d'Couleurs" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://www.crionsdcouleurs.fr/presentation" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.crionsdcouleurs.fr/presentation" />
        <meta property="og:title" content="Association - Crions d'Couleurs" />
        <meta property="og:description" content="Association de jeunes organisant des colonies de vacances accessibles pour enfants de 6-17 ans en Ille-et-Vilaine. Engagement, mixité sociale et droit aux vacances pour tous." />
        <meta property="og:image" content="https://www.crionsdcouleurs.fr/images/photo_groupe.jpg" />
        <meta property="og:image:alt" content="Groupe de bénévoles et enfants Crions d'Couleurs lors d'une colonie de vacances" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Crions d'Couleurs" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.crionsdcouleurs.fr/presentation" />
        <meta name="twitter:title" content="Association - Crions d'Couleurs" />
        <meta name="twitter:description" content="Association de jeunes organisant des colonies de vacances accessibles pour enfants de 6-17 ans en Ille-et-Vilaine. Engagement et mixité sociale." />
        <meta name="twitter:image" content="https://www.crionsdcouleurs.fr/images/photo_groupe.jpg" />
        <meta name="twitter:site" content="@crionsdcouleurs" />

        {/* Geographic Tags */}
        <meta name="geo.region" content="FR-35" />
        <meta name="geo.placename" content="Vitré, Ille-et-Vilaine, Bretagne" />
        <meta name="geo.position" content="48.1253;-1.2097" />
        <meta name="ICBM" content="48.1253, -1.2097" />

        {/* Additional SEO */}
        <meta name="language" content="French" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="general" />

        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Crions d'Couleurs",
            "description": "Association de jeunes du Pays de Vitré organisant des séjours de vacances accessibles dans une dynamique de mixité sociale",
            "url": "https://www.crionsdcouleurs.fr",
            "logo": "https://www.crionsdcouleurs.fr/images/logo_transparent.png",
            "foundingDate": "2010",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Vitré",
              "addressRegion": "Bretagne",
              "postalCode": "35500",
              "addressCountry": "FR"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "crionsdcouleurs@gmail.com",
              "contactType": "Customer Service",
              "areaServed": "FR",
              "availableLanguage": "French"
            },
            "sameAs": [
              "https://www.instagram.com/crionsdcouleurs"
            ],
            "areaServed": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": "48.1253",
                "longitude": "-1.2097"
              },
              "geoRadius": "50000"
            }
          })}
        </script>

        {/* Structured Data - LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NGO",
            "name": "Crions d'Couleurs",
            "image": "https://www.crionsdcouleurs.fr/images/logo_transparent.png",
            "priceRange": "€",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Vitré",
              "addressRegion": "Ille-et-Vilaine",
              "postalCode": "35500",
              "addressCountry": "FR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 48.1253,
              "longitude": -1.2097
            },
            "url": "https://www.crionsdcouleurs.fr",
            "email": "crionsdcouleurs@gmail.com",
            "foundingDate": "2010",
            "description": "Association composée de jeunes du Pays de Vitré organisant chaque année des colonies de vacances accessibles pour enfants de 6 à 17 ans",
            "knowsAbout": ["Colonies de vacances", "Séjours jeunesse", "Engagement associatif", "Mixité sociale", "Animation jeunesse"],
            "memberOf": {
              "@type": "Organization",
              "name": "Mouvement associatif français"
            }
          })}
        </script>

        {/* Structured Data - BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": "https://www.crionsdcouleurs.fr"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Présentation de l'association",
                "item": "https://www.crionsdcouleurs.fr/presentation"
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb" aria-label="Fil d'Ariane">
        <ol itemScope itemType="https://schema.org/BreadcrumbList">
          <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <Link to="/" itemProp="item">
              <span itemProp="name">Accueil</span>
            </Link>
            <meta itemProp="position" content="1" />
          </li>
          <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <span itemProp="name">Présentation de l'association</span>
            <meta itemProp="position" content="2" />
          </li>
        </ol>
      </nav>

      {/* Navbar */}
      <nav className={`presentation-navbar ${isScrolled ? 'scrolled' : ''}`} role="navigation" aria-label="Navigation principale">
        <div className="nav-container">
          <div className="nav-logo">
            <img
              src="/images/logo_transparent.png"
              alt="Logo Crions D'Couleurs - Association colonies de vacances Vitré Ille-et-Vilaine"
              className="logo-img"
              width="50"
              height="50"
              loading="eager"
            />
            <span className="logo-text">Crions D'Couleurs</span>
          </div>

          {/* Desktop Menu */}
          <div className="nav-menu desktop-menu">
            <button onClick={() => scrollToSection('accueil')} className="nav-link">
              Accueil
            </button>
            <button onClick={() => scrollToSection('about')} className="nav-link">
              Notre association
            </button>
            <button onClick={() => scrollToSection('help')} className="nav-link">
              Nous aider
            </button>
            <Link to="/sejours" className="nav-link">
              Séjours
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard" className="nav-btn">
                Mon espace
              </Link>
            ) : (
              <Link to="/login" className="nav-btn">
                Se connecter
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="mobile-menu">
            <button onClick={() => scrollToSection('accueil')} className="mobile-nav-link">
              Accueil
            </button>
            <button onClick={() => scrollToSection('about')} className="mobile-nav-link">
              Notre association
            </button>
            <button onClick={() => scrollToSection('help')} className="mobile-nav-link">
              Nous aider
            </button>
            <Link to="/sejours" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
              Séjours
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard" className="mobile-nav-btn" onClick={() => setIsMobileMenuOpen(false)}>
                Mon espace
              </Link>
            ) : (
              <Link to="/login" className="mobile-nav-btn" onClick={() => setIsMobileMenuOpen(false)}>
                Se connecter
              </Link>
            )}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <header
        id="accueil"
        className="hero-presentation"
        style={{ backgroundImage: 'url(/images/photo_groupe.jpg)' }}
        role="banner"
      >
        <div className="hero-overlay">
          <div className="hero-content">
            <h1 className="hero-title">CRIONS D'COULEURS</h1>
            <p className="hero-subtitle">"La jeunesse s'engage"</p>
          </div>
        </div>
      </header>

      {/* Statistics Section */}
      <section className="statistics" ref={statsRef}>
        <div className="stat-item">
          <h3 className="stat-label">Enfants partis en<br />colo</h3>
          <p className="stat-number">+1 100</p>
        </div>
        <div className="stat-item">
          <h3 className="stat-label">Sourires</h3>
          <p className="stat-number">∞</p>
        </div>
        <div className="stat-item">
          <h3 className="stat-label">Membres du<br />bureau</h3>
          <p className="stat-number">7</p>
        </div>
        <div className="stat-item">
          <h3 className="stat-label">Depuis</h3>
          <p className="stat-number">2010</p>
        </div>
      </section>

      {/* Main Content */}
      <main className="container-presentation">
        {/* What We Do Section */}
        <article id="about" className="what-we-do">
          <div className="section-title-wrapper">
            <p className="section-label">✱ Notre association</p>
            <h2 className="section-title">CE QUE NOUS FAISONS</h2>
          </div>

          <div className="activities-grid">
            <section className="activity-card">
              <h3>Organisation de colonies de vacances</h3>
              <p>
                Chaque année au mois d'août depuis 15 ans nous organisons une colonie de vacances.
                Dans une optique de mixité sociale et de droit aux vacances pour tous et toutes
                nous faisons en sorte de proposer au maximum le prix de nos séjours
              </p>
            </section>

            <section className="activity-card">
              <h3>Organisation d'événements divers</h3>
              <p>
                Afin de financer les colonies et de dynamiser le territoire du pays de Vitré nous
                organisons tout au long de l'année divers événements (loto, tournois de foot,
                vente de vin chaud, cinéma en plein air,...)
              </p>
            </section>

            <section className="activity-card">
              <h3>Favoriser l'engagement de la jeunesse</h3>
              <p>
                L'association est principalement composée de jeunes de 16 à 25 ans qui participent
                en tant que bénévoles pendant les colos ou les événements. Ce sont ainsi plusieurs
                centaines de jeunes qui ont en l'espace de 15 ans participer à la vie de
                l'association
              </p>
            </section>
          </div>
        </article>

        {/* Coming Soon Section */}
        <section className="coming-soon">
          <div className="coming-soon-content">
            <h2>Encore un peu de patience, la colo 2026 se prépare !</h2>
            <p className="coming-soon-text">
              Notre équipe travaille d'arrache-pied pour vous proposer un séjour inoubliable.
              Les inscriptions seront bientôt ouvertes !
            </p>
            <button className="btn-disabled" disabled>
              Inscriptions à venir
            </button>
          </div>
          <div className="coming-soon-image">
            <img
              src="/images/photo_resto.jpg"
              alt="Équipe de bénévoles Crions d'Couleurs préparant une colonie de vacances pour enfants"
              loading="lazy"
              width="600"
              height="400"
            />
          </div>
        </section>

        {/* How to Help Section */}
        <section id="help" className="how-to-help">
          <h2 className="section-title">Comment nous aider ?</h2>

          <div className="help-grid">
            <article className="help-card">
              <div className="help-image">
                <img
                  src="/images/photo_groupe_soir.jpg"
                  alt="Groupe d'enfants et bénévoles Crions d'Couleurs en soirée pendant une colonie de vacances"
                  loading="lazy"
                  width="400"
                  height="320"
                />
              </div>
              <div className="help-content">
                <h3>Parler de nous</h3>
                <p>
                  La majorité des bénévoles a entendu parler de Crions D'Couleurs grâce au bouche
                  à oreille alors n'hésite pas à parler un maximum de nous on vous sera très
                  reconnaissant
                </p>
              </div>
            </article>

            <article className="help-card">
              <div className="help-image">
                <img
                  src="/images/photo_cuisine.webp"
                  alt="Bénévoles en cuisine lors d'une colonie de vacances Crions d'Couleurs Vitré"
                  loading="lazy"
                  width="400"
                  height="320"
                />
              </div>
              <div className="help-content">
                <h3>Devenir bénévole</h3>
                <p>
                  Aucune compétence particulière n'est demandée (si ce n'est de la bonne volonté)
                  N'hésite pas à nous contacter on sera très heureux de t'accueillir
                </p>
              </div>
            </article>

            <article className="help-card">
              <div className="help-image">
                <img
                  src="/images/photo_benevole_dos.webp"
                  alt="Bénévole Crions d'Couleurs lors d'un événement associatif à Vitré"
                  loading="lazy"
                  width="400"
                  height="320"
                />
              </div>
              <div className="help-content">
                <h3>Participer aux événements</h3>
                <p>
                  Participer aux événements que nous organisons est déjà d'une grande aide pour
                  notre association, cela nous permet de continuer à la faire vivre d'année en
                  année.
                </p>
              </div>
            </article>
          </div>
        </section>

        {/* Become Volunteer Section */}
        <section className="become-volunteer">
          <div className="volunteer-image">
            <img
              src="/images/photo_benevole.jpg"
              alt="Bénévoles Crions d'Couleurs"
              loading="lazy"
              width="600"
              height="450"
            />
          </div>
          <div className="volunteer-content">
            <h2>REJOINS L'AVENTURE !</h2>
            <p className="volunteer-text">
              Envie de vivre une expérience unique et enrichissante ? Rejoins notre équipe de
              bénévoles passionnés ! Pas besoin de compétences particulières, juste de la bonne
              humeur et l'envie de partager des moments inoubliables avec les enfants.
            </p>
            <p className="volunteer-text">
              Que tu veuilles participer à nos colonies d'été, nous aider lors de nos événements
              tout au long de l'année, ou simplement découvrir l'association, il y a une place
              pour toi chez Crions D'Couleurs !
            </p>
            <button className="volunteer-btn" onClick={() => setIsContactModalOpen(true)}>
              Rejoignez-nous
            </button>
          </div>
        </section>
      </main>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div className="modal-overlay" onClick={() => setIsContactModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setIsContactModalOpen(false)}>
              ×
            </button>
            <h3>Contactez-nous !</h3>
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div className="contact-details">
                  <p className="contact-label">Email</p>
                  <a href="mailto:crionsdcouleurs@gmail.com" className="contact-link">
                    crionsdcouleurs@gmail.com
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📱</div>
                <div className="contact-details">
                  <p className="contact-label">Instagram</p>
                  <a
                    href="https://www.instagram.com/crionsdcouleurs"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-link"
                  >
                    @crionsdcouleurs
                  </a>
                </div>
              </div>
            </div>
            <p className="modal-footer-text">
              N'hésite pas à nous contacter, on sera ravis de t'accueillir !
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer noSidebarMargin={true} />
    </div>
  );
};

export default PresentationAssociation;
